import React, { useEffect, useState, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { Button } from '@design-system';
import { ButtonStyle } from '@ds/elements/Button/Button.types';
import { ProfileActionType, useUserProfileStore } from 'src/stores/UserProfile';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import styles from './ProfileChangeAlert.module.scss';

const AlertMessages = {
  NEW_DEPENDENT: 'You added a dependent to your profile, please make sure the changes were intended.',
  PROFILE_UPDATED: 'You made some changes to your profile, please make sure the changes were intended.',
  UPDATE_PROFILE: 'Make changes to your profile if needed and save them.',
};

const ProfileChangeAlert: React.FC = () => {
  const [clickOutsideEnabled, setClickOutsideEnabled] = useState<boolean>(true);
  const { editMode, displayUpdateButton, enableSaveChanges, toggleEditMode, handleProfileAction, profileInfo } =
    useUserProfileStore();
  const { isDesktop } = useScreenSize();
  const updateButtonContainerRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (updateButtonContainerRef.current && updateButtonContainerRef.current.contains(event.target as Node)) {
        return;
      }

      const addDependentsButton = document.querySelector('.dependent-button');
      const datePickerComponent = document.querySelector('.p-datepicker');
      const userProfileContent = document.querySelector(`#user-profile-content ${isDesktop ? '.p-tabview-panel' : ''}`);
      const dialogContent = document.querySelector('.p-dialog-mask.p-dialog-center.p-component-overlay');
      const target = event.target as Node;
      const allowToClick =
        addDependentsButton?.contains(target) ||
        datePickerComponent?.contains(target) ||
        userProfileContent?.contains(target) ||
        dialogContent?.contains(target);

      if (allowToClick) {
        return;
      }

      if (editMode && alertRef.current && !alertRef.current.contains(target)) {
        event.preventDefault();
        event.stopPropagation();
        setClickOutsideEnabled(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [editMode]);

  const profileContainerClass = useMemo(() => {
    return classNames(
      styles['profile-change-alert-container'],
      editMode && styles['edit-mode'],
      isDesktop && editMode && '!sticky !top-[330px] !z-[1000] bg-white'
    );
  }, [editMode, isDesktop]);

  const getAlertText = () => {
    if (profileInfo?.id === -1) {
      return AlertMessages.NEW_DEPENDENT;
    } else if (enableSaveChanges) {
      return AlertMessages.PROFILE_UPDATED;
    } else {
      return AlertMessages.UPDATE_PROFILE;
    }
  };

  const onAnimationEnd = () => {
    setClickOutsideEnabled(true);
  };

  if (!displayUpdateButton) return null;

  return (
    <div className={profileContainerClass} id="profile-change-alert-bar">
      {editMode ? (
        <div
          ref={alertRef}
          className={classNames(styles['profile-change-alert'], !clickOutsideEnabled ? styles['flicker'] : '')}
          onAnimationEnd={onAnimationEnd}
        >
          <div className={styles['text-content']}>
            <div className={styles['user-icon']}></div>
            <p>{getAlertText()}</p>
          </div>

          <div className={styles['profile-change-alert-buttons']}>
            <Button
              styleType="secondary"
              variant="contained"
              className={styles['button']}
              disabled={!enableSaveChanges}
              onClick={() => handleProfileAction(ProfileActionType.SAVE)}
            >
              YES, SAVE CHANGES
            </Button>
            <Button
              styleType="secondary"
              variant="contained"
              className={styles['button']}
              onClick={() => {
                toggleEditMode();
                handleProfileAction(ProfileActionType.UNDO);
              }}
            >
              UNDO
            </Button>
          </div>
        </div>
      ) : (
        <div ref={updateButtonContainerRef}>
          <Button className={styles['button']} styleType={ButtonStyle.secondary} onClick={toggleEditMode}>
            <span className={styles['note-pencil-icon']}></span>
            <span>UPDATE</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfileChangeAlert;
